import { create } from 'zustand';
import { HOME_SURFACE, type SurfaceId } from './routes';

/**
 * Transient view state only: which surface is showing, and what is open on it. Partner-Ansicht's rotation used
 * to live here and moved to `Settings` in Phase 3 (C60) — the rule below is why: which way the iPad faces is a
 * fact about the room, and losing it on relaunch was wrong rather than deliberate.
 * Nothing here is persisted — it is deliberately the one store that may be lost on relaunch,
 * because reopening on Sprechen is always the right behaviour.
 *
 * Everything durable belongs in IndexedDB via a repository (INV-04).
 */
export type ForcedReason = 'speechFailure' | null;

/** Which board the keyboard is showing. `GEO-19`. */
export type KeyboardLayer = 'letters' | 'digits';

interface UiState {
  surface: SurfaceId;
  /** Why we are on this surface, when it was not a deliberate tap. */
  forcedBy: ForcedReason;
  /**
   * Letters or digits (`GEO-19`, `GEO-21`).
   *
   * Here rather than in `Settings` deliberately, and it is the one place the rule at the top of this file is
   * a feature rather than a cost: a relaunch, a crash into safe mode or a restore all come up on the letters,
   * which is the only board the person can spell with. A caregiver never sets this, so `CON-11` does not
   * claim it — it is view state, exactly like which surface is showing.
   *
   * Nothing resets it otherwise. Speaking a sentence leaves the board where the person put it, because a
   * board that changes itself between sentences is a board they have to check.
   */
  keyboardLayer: KeyboardLayer;
  /** Which category is open on Sätze. Null shows the category list. */
  openCategoryId: string | null;
  /** Where to return to when Partner-Ansicht closes. */
  previousSurface: SurfaceId;
  /**
   * Which Betreuer-Modus tab to open on, when something outside care mode is sending the caregiver there.
   *
   * Only the first-launch restore offer uses it: sending somebody to a settings screen and asking them to
   * find the right tab is how a one-tap offer becomes a hunt. Cleared when care mode reads it.
   */
  careSection: string | null;
  /**
   * Bumped when the whole database has been replaced by a restore.
   *
   * Components that read a store directly — the quick fires in the persistent bar — depend on this so they
   * re-read instead of showing the previous device's content until something happens to remount them.
   */
  dataVersion: number;
  goTo: (surface: SurfaceId) => void;
  toggleKeyboardLayer: () => void;
  openCare: (section: string) => void;
  takeCareSection: () => string | null;
  dataReplaced: () => void;
  forceTo: (surface: SurfaceId, reason: ForcedReason) => void;
  back: () => void;
  openCategory: (categoryId: string | null) => void;
}

export const useUiStore = create<UiState>((set, get) => ({
  surface: HOME_SURFACE,
  forcedBy: null,
  keyboardLayer: 'letters',
  openCategoryId: null,
  previousSurface: HOME_SURFACE,
  careSection: null,
  dataVersion: 0,

  openCare: (section) =>
    set((s) => ({
      surface: 'betreuer',
      forcedBy: null,
      previousSurface: s.surface === 'betreuer' ? s.previousSurface : s.surface,
      careSection: section,
    })),

  dataReplaced: () => set((s) => ({ dataVersion: s.dataVersion + 1 })),

  takeCareSection: () => {
    const { careSection } = get();
    if (careSection !== null) set({ careSection: null });
    return careSection;
  },

  goTo: (surface) =>
    set((s) => ({
      surface,
      forcedBy: null,
      previousSurface: s.surface === surface ? s.previousSurface : s.surface,
      // Reopening Sätze starts at the category list rather than wherever the person was last —
      // a board that resumes mid-navigation is harder to predict than one that always starts
      // from the same place.
      openCategoryId: surface === 'saetze' ? null : s.openCategoryId,
    })),

  forceTo: (surface, reason) =>
    set((s) => ({
      surface,
      forcedBy: reason,
      previousSurface: s.surface === surface ? s.previousSurface : s.surface,
    })),

  back: () => set({ surface: get().previousSurface, forcedBy: null }),

  openCategory: (categoryId) => set({ openCategoryId: categoryId }),

  toggleKeyboardLayer: () =>
    set((s) => ({ keyboardLayer: s.keyboardLayer === 'letters' ? 'digits' : 'letters' })),
}));
